import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chatService } from 'services/chat/chatService';
import { OfferStatus } from '../services';
import { Offer } from '../types';
import { OfferCard } from './OfferCard';

interface OffersListProps {
	offers: Offer[];
	isLoading: boolean;
	error: string | null;
	requestStatus?: OfferStatus;
	requestId: string;
	userId: string;
	// Add optional request data that contains dates
	requestStartDate?: string;
	requestEndDate?: string;
	requestOriginalDate?: {
		start_date: string;
		end_date: string;
	};
	onRefreshOffers?: () => void; // Add callback to refresh offers data
}

export const OffersList: React.FC<OffersListProps> = ({
	offers,
	isLoading,
	requestId,
	requestStatus = 'ongoing',
	userId,
	requestStartDate,
	requestEndDate,
	requestOriginalDate,
	onRefreshOffers,
}) => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const theme = useTheme();
	const [chatLoading, setChatLoading] = useState(false);
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isCompleted = requestStatus === 'completed';
	const isArchived = requestStatus === 'archived';

	const handleChat = async (offerId: string, specialistId?: string) => {
		setChatLoading(true);
		
		// Use specialistId if provided, otherwise fall back to userId
		const userIdToUse = specialistId || userId;
		
		chatService
			.getChannelIdByRequestAndUserId(requestId, userIdToUse)
			.then((channel) => {
				navigateWithLanguage(`/farmer/chats?channelId=${channel}`);
			})
			.catch((error) => {
				console.error('Error fetching channel ID:', error);
			})
			.finally(() => setChatLoading(false));
	};

	return (
		<Box>
			<Typography
				variant="h6"
				sx={{
					mb: 2,
					fontWeight: 500,
					fontSize: isMobile ? '1rem' : undefined,
				}}
			>
				{t('offers')}
			</Typography>

			{/* Empty state */}
			{(!offers || offers.length === 0) && !isLoading && (
				<Box sx={{ py: 4, textAlign: 'center' }}>
					<Typography variant="body1" color="#343A40">
						{t('noOffers')}
					</Typography>
					<Typography variant="body2" color="#7B809A">
						{t('noOffersDescription')}
					</Typography>
				</Box>
			)}

			{/* Offers list */}
			{offers &&
				offers.map((offer, index) => (
					<div key={`${offer.id}_${index}`}>
						<OfferCard
							offer={{
								...offer,
								// Add request dates to the offer object if they're not already there
								start_date: offer.start_date || requestStartDate,
								end_date: offer.end_date || requestEndDate,
								// Add original_date if it exists in the request
								original_date: offer.original_date || requestOriginalDate,
								// Set payment_status to "unpaid" if original_date exists
								payment_status: offer.payment_status || (requestOriginalDate ? 'unpaid' : undefined)
							}}
							onChat={handleChat}
							isMobile={isMobile}
							isCompleted={isCompleted}
							isArchived={isArchived}
							chatLoading={chatLoading}
							onRefresh={onRefreshOffers}
						/>
					</div>
				))}

			{/* Loading indicator */}
			{isLoading && (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
					<CircularProgress size={30} sx={{ color: '#3da08f' }} />
				</Box>
			)}
		</Box>
	);
};
